import { useEffect, useRef } from 'react';

/**
 * useAutoUpdate - Polls /asset-manifest.json to detect new deployments.
 *
 * Compares the current JS bundle hash with the server's latest.
 * When a mismatch is detected (new build deployed), reloads the page.
 *
 * @param {number} intervalMs - Polling interval (default 120000 = 2 minutes)
 */
export function useAutoUpdate(intervalMs = 120000) {
  const currentHashRef = useRef(null);

  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV !== 'production') return;

    // Extract current JS hash from the running script tag
    const scripts = document.querySelectorAll('script[src*="/static/js/main."]');
    if (scripts.length > 0) {
      currentHashRef.current = scripts[0].getAttribute('src');
    }

    if (!currentHashRef.current) return;

    const checkForUpdate = async () => {
      try {
        // Cache-bust the manifest fetch so we always get the latest
        const res = await fetch(`/asset-manifest.json?_t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;

        const manifest = await res.json();
        const serverJs = manifest?.files?.['main.js'];

        if (serverJs && serverJs !== currentHashRef.current) {
          console.log(
            `[AutoUpdate] New build detected: ${serverJs} (current: ${currentHashRef.current}). Reloading...`
          );
          window.location.reload();
        }
      } catch {
        // Network error - skip this check
      }
    };

    const timer = setInterval(checkForUpdate, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
}
